import { DEFAULT_WIDGET_SETTINGS } from '@flexbyt/shared';
import {
    ReviewStars,
    ReviewerAvatar,
    SourceGlyph,
    cx,
} from '@flexbyt/widget-preview';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { SourceFlow } from '../components/integrations/SourceFlow.js';
import { Breadcrumbs } from '../components/site/Breadcrumbs.js';
import {
    ButtonAnchor,
    ButtonHashLink,
    ButtonLink,
    SiteLink,
} from '../components/ui/button.js';
import {
    ArrowIcon,
    BrowserWindow,
    HERO_PILL,
    HeroBackdrop,
    HeroFacts,
} from '../components/ui/hero.js';
import { Section } from '../components/ui/layout.js';
import { LayoutSketch } from '../components/widgets/LayoutSketch.js';
import { AvatarRow } from '../components/ui/review.js';
import { Heading, MarkPhrase, Text } from '../components/ui/type.js';
import {
    combinationPath,
    combinationsForSource,
} from '../content/combinations.js';
import {
    CHROME_EXTENSION_URL,
    DIRECT_INTEGRATIONS,
    EXTENSION_INTEGRATIONS,
    integrationBySlug,
    integrationKind,
    integrationPath,
    type IntegrationContent,
} from '../content/integrations.js';
import { PLATFORM_CONTENT, SEO_PLATFORMS } from '../content/platforms.js';
import { sampleSetForSource } from '../content/sample-sets.js';
import {
    WIDGET_BUILDER_PATH,
    WIDGET_CONTENT,
    widgetPath,
} from '../content/widgets.js';
import { track } from '../lib/analytics.js';
import { fetchImportPreviewConfig } from '../lib/import-preview.js';
import { breadcrumbSchema, faqSchema, jsonLd, seo } from '../lib/seo.js';
import { site, startUrl } from '../lib/site.js';

/**
 * One review source, as an acquisition page.
 *
 * ===========================================================================
 * A mini-product, with the interactive part at the top
 * ===========================================================================
 * Paste your listing, tick the reviews you would show, style the widget, take
 * it to signup. The explanatory copy sits underneath, where it earns the
 * ranking without standing between the visitor and the thing they came for.
 *
 * ===========================================================================
 * The homepage's vocabulary, not a document's
 * ===========================================================================
 * The same grid-and-glow hero, pill badges, source tiles and rounded closing
 * card the homepage uses, so arriving here from a search result looks like the
 * same product. The hero's right half shows this source's own sample reviews
 * as they would sit on a website — the claim, drawn, before any of it is read.
 *
 * ===========================================================================
 * The two kinds of page are visibly different
 * ===========================================================================
 * `kind` comes from the product, and it changes the page rather than adding a
 * footnote: a direct source gets a field to paste a listing into, an extension
 * source gets an explanation of how collection actually works, in the same
 * position. Somebody who expects "paste a link" and discovers "install an
 * extension" after signing up has been misled by us.
 */
export const Route = createFileRoute('/integrations/$slug/')({
  loader: async ({ params }) => {
    const integration = integrationBySlug(params.slug);
    if (!integration) throw notFound();

    /*
      Asked on the server, so the page knows before it paints whether it can
      offer to read a listing. A button that appears a second after the paste
      field is one somebody has already scrolled past, and the two versions of
      this page — "we will read it now" and "we will read it once you sign up"
      — say different things in the same place.

      It never throws: `fetchImportPreviewConfig` answers "disabled" for every
      failure, so an API that is down costs this page a feature rather than a
      render.
    */
    const preview = await fetchImportPreviewConfig();
    return { integration, preview };
  },
  head: ({ loaderData }) => {
    const integration = loaderData?.integration;
    if (!integration) return {};

    const { meta, links } = seo({
      title: `${integration.name} reviews on your website`,
      description: integration.intro,
      path: integrationPath(integration),
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Integrations', path: '/integrations' },
              { name: integration.name, path: integrationPath(integration) },
            ])
          ),
        },
        {
          type: 'application/ld+json',
          children: jsonLd(faqSchema(integration.faqs)),
        },
      ],
    };
  },
  component: IntegrationPage,
});

function IntegrationPage() {
  const { integration, preview } = Route.useLoaderData();
  const isDirect = integrationKind(integration.id) === 'connect';
  const siblings = (isDirect ? DIRECT_INTEGRATIONS : EXTENSION_INTEGRATIONS).filter(
    (entry) => entry.slug !== integration.slug
  );
  const combinations = combinationsForSource(integration.id);

  const startClicked = (where: string) =>
    track('hero_cta_clicked', {
      source: integration.id,
      surface: `integration-${integration.slug}${where}`,
    });

  return (
    <>
      {/* ------------------------------------------------------------ Hero */}
      <Section
        size="tight"
        width="wide"
        className="relative isolate overflow-x-clip pt-10 md:pt-14"
      >
        <HeroBackdrop />

        <Breadcrumbs
          trail={[
            { name: 'Home', path: '/' },
            { name: 'Integrations', path: '/integrations' },
            { name: integration.name, path: integrationPath(integration) },
          ]}
        />

        <div className="mt-10 grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="min-w-0 lg:col-span-7">
            <p className={cx('rj-rise', HERO_PILL)}>
              <span className="grid size-6 place-items-center rounded-full bg-ink-25">
                <SourceGlyph source={integration.id} className="size-3.5" />
              </span>
              {integration.name}
              <span aria-hidden className="text-ink-300">
                ·
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden
                  className={cx(
                    'size-1.5 rounded-full',
                    isDirect ? 'bg-positive-600' : 'bg-brand'
                  )}
                />
                {isDirect ? 'Paste a link' : 'Chrome extension'}
              </span>
            </p>

            <Heading
              level={1}
              size="hero"
              className="rj-rise rj-delay-1 mt-6 leading-[0.98] font-extrabold tracking-[-0.045em]"
            >
              <MarkPhrase text={integration.headline} phrase={integration.name} />
            </Heading>

            <Text
              size="lead"
              className="rj-rise rj-delay-2 mt-6 max-w-2xl sm:text-xl sm:leading-9"
            >
              {integration.intro}
            </Text>

            <div className="rj-rise rj-delay-3 mt-9 flex flex-col gap-3 sm:flex-row">
              <ButtonAnchor
                href={startUrl(`integration-${integration.slug}-hero`)}
                size="lg"
                className="h-14 px-8"
                onClick={() => startClicked('-hero')}
              >
                Start free
                <ArrowIcon />
              </ButtonAnchor>
              <ButtonHashLink
                hash="try-it"
                variant="secondary"
                size="lg"
                className="h-14 px-8"
              >
                {isDirect
                  ? `Try it with your ${integration.name} link`
                  : 'See how it works'}
              </ButtonHashLink>
            </div>

            <HeroFacts
              className="rj-rise rj-delay-4 mt-7"
              facts={[
                'Free plan, no card',
                isDirect ? 'New reviews come in on their own' : 'Free Chrome extension',
                // Only the first letter: the rest names brands ("Google Business Profile").
                `For ${integration.audience.charAt(0).toLowerCase()}${integration.audience.slice(1)}`
              ]}
            />

            <div className="rj-rise rj-delay-4 mt-8 flex items-center gap-3">
              <AvatarRow
                people={sampleSetForSource(integration.id)
                  .reviews.slice(0, 4)
                  .map((review) => ({ name: review.authorName, avatar: review.authorAvatar }))}
                size={28}
              />
              <p className="text-caption text-ink-500">
                Trusted by thousands of businesses around the world
              </p>
            </div>
          </div>

          <div className="rj-rise rj-delay-3 min-w-0 lg:col-span-5">
            <HeroReviews integration={integration} isDirect={isDirect} />
          </div>
        </div>
      </Section>

      {/* ------------------------------------------------- The interactive */}
      <Section
        id="try-it"
        size="base"
        aria-labelledby="try-heading"
        className="bg-[linear-gradient(180deg,#ffffff_0%,#f4f6fd_22%,#f6f5fc_78%,#ffffff_100%)]"
      >
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-caption font-semibold tracking-[0.08em] text-brand uppercase">
            Try it now
          </p>
          <Heading level={2} id="try-heading" className="mt-3">
            {isDirect
              ? `Your ${integration.name} reviews, as a widget, in a minute.`
              : `How ${integration.name} reviews reach your website.`}
          </Heading>
          <Text className="mx-auto mt-4 max-w-2xl">
            {isDirect
              ? 'Point us at your listing, choose the reviews worth showing, and style the widget — all before you make an account.'
              : 'The extension collects them from your own reviews page. Below, sample reviews show what you will be choosing from and how the widget looks.'}
          </Text>
        </div>

        <div className="relative mt-12">
          <div>
            <div className="rounded-[2rem] bg-white px-5 py-8 shadow-[0_1px_2px_rgba(10,10,11,0.04),0_40px_100px_-40px_rgba(20,40,120,0.3)] sm:px-10 sm:py-10">
              <SourceFlow integration={integration} preview={preview} />
            </div>
          </div>
        </div>
      </Section>

      {/* ---------------------------------------------------- Use cases */}
      <Section tone="raised" aria-labelledby="uses-heading">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="text-caption font-semibold tracking-[0.08em] text-brand uppercase">
              Who it is for
            </p>
            <Heading level={2} id="uses-heading" className="mt-3">
              Who puts {integration.name} reviews on their site.
            </Heading>
          </div>
          <Text className="lg:col-span-5">{integration.audience}.</Text>
        </div>

        <ul className="mt-12 grid gap-4 md:grid-cols-3">
          {integration.useCases.map((useCase, index) => (
            <li
              key={useCase.title}
              className="group relative overflow-hidden rounded-2xl bg-white p-7 shadow-[0_1px_2px_rgba(10,10,11,0.04),0_12px_32px_-20px_rgba(20,40,120,0.2)] transition-shadow duration-[var(--duration-fast)] hover:shadow-[0_24px_48px_-28px_rgba(20,40,120,0.35)]"
            >
              <span
                aria-hidden
                className="pointer-events-none absolute -top-16 -right-16 size-40 rounded-full bg-[radial-gradient(closest-side,rgba(31,91,240,0.08),transparent)] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              />
              <span className="grid size-11 place-items-center rounded-xl bg-brand/[0.07]">
                <UseCaseIcon index={index} />
              </span>
              <p className="mt-6 text-title text-ink-950">{useCase.title}</p>
              <p className="mt-2 text-body-sm text-ink-600">{useCase.body}</p>
            </li>
          ))}
        </ul>

        {integration.multiple ? (
          <div className="mt-4 grid items-center gap-8 rounded-2xl bg-white p-7 shadow-[0_1px_2px_rgba(10,10,11,0.04),0_12px_32px_-20px_rgba(20,40,120,0.2)] sm:p-9 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h3 id="multiple-heading" className="text-title text-ink-950">
                More than one {integration.name} listing?
              </h3>
              <p className="mt-3 text-body text-ink-600">{integration.multiple}</p>
              {/*
                Said carefully. The product genuinely supports several connections
                of one platform — `ReviewSource` carries no unique index on
                `{ siteId, source }` and rows are told apart by `displayName`. It
                does not have location groups, per-location dashboards or anything
                that could be called multi-location management, so neither does
                this paragraph.
              */}
              <p className="mt-3 text-caption text-ink-500">
                Each listing is connected on its own. There is no automatic
                multi-location setup — you add them one at a time.
              </p>
            </div>
            <ul aria-hidden className="space-y-2 lg:col-span-4">
              {['Location one', 'Location two', 'Location three'].map((label, index) => (
                <li
                  key={label}
                  className="flex items-center gap-3 rounded-xl bg-ink-25 px-3.5 py-2.5"
                  style={{ marginLeft: `${index * 0.75}rem` }}
                >
                  <span className="grid size-7 place-items-center rounded-lg bg-white shadow-xs">
                    <SourceGlyph source={integration.id} className="size-3.5" />
                  </span>
                  <span className="text-caption font-medium text-ink-800">
                    {integration.name} — {label}
                  </span>
                  <span className="ml-auto size-1.5 rounded-full bg-positive-600" />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Section>

      {/* -------------------------------------------------------- FAQ */}
      <Section aria-labelledby="faq-heading">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <p className="text-caption font-semibold tracking-[0.08em] text-brand uppercase">
                FAQ
              </p>
              <Heading level={2} id="faq-heading" className="mt-3">
                Questions about {integration.name}.
              </Heading>
              <Text size="small" tone="muted" className="mt-4">
                Something else?{' '}
                <a
                  href={`mailto:${site.email}`}
                  className="font-medium text-ink-950 underline underline-offset-[3px]"
                >
                  Write to us
                </a>{' '}
                — a person answers.
              </Text>
            </div>
          </div>

          <div className="space-y-3 lg:col-span-7">
            {integration.faqs.map((faq, index) => (
              <details
                key={faq.q}
                open={index === 0}
                className="group rounded-2xl bg-ink-25 transition-[background-color,box-shadow] duration-[var(--duration-fast)] hover:bg-ink-50 open:bg-white open:shadow-[0_1px_2px_rgba(10,10,11,0.04),0_24px_48px_-28px_rgba(20,40,120,0.3)]"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 px-6 py-5 text-title text-ink-950 [&::-webkit-details-marker]:hidden">
                  {faq.q}
                  <span className="grid size-8 shrink-0 place-items-center rounded-full bg-white shadow-xs transition-transform duration-200 group-open:rotate-45">
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      className="size-3.5 text-ink-700"
                      aria-hidden
                    >
                      <path d="M8 3v10M3 8h10" />
                    </svg>
                  </span>
                </summary>
                <p className="-mt-1 max-w-prose px-6 pb-6 text-body text-ink-600">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </Section>

      {/* ----------------------------------------------- Internal linking */}
      <Section tone="raised" aria-labelledby="more-heading">
        <div className="grid gap-4 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="text-caption font-semibold tracking-[0.08em] text-brand uppercase">
              Keep exploring
            </p>
            <Heading level={2} id="more-heading" size="display" className="mt-3">
              Where to next.
            </Heading>
          </div>
          <Text className="lg:col-span-5">
            Every layout, every website builder and every other source{' '}
            {integration.name} reviews can sit beside.
          </Text>
        </div>

        {/* Layouts, drawn — the same sketches as the builder's picker. */}
        <div className="mt-12">
          <p className="text-caption font-semibold text-ink-950">Widget layouts</p>
          <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {WIDGET_CONTENT.map((widget) => (
              <li key={widget.slug}>
                <SiteLink
                  to={widgetPath(widget)}
                  className="group flex h-full flex-col rounded-2xl bg-white p-3 shadow-[0_1px_2px_rgba(10,10,11,0.04),0_12px_32px_-22px_rgba(20,40,120,0.25)] transition-shadow duration-[var(--duration-fast)] hover:shadow-[0_1px_2px_rgba(10,10,11,0.04),0_20px_40px_-18px_rgba(20,40,120,0.35)]"
                >
                  <LayoutSketch type={widget.type} active className="h-24" />
                  <span className="mt-3 flex items-center justify-between gap-2 px-1 text-body-sm font-semibold text-ink-950">
                    {widget.seoName}
                    <span className="text-ink-400 transition-transform duration-[var(--duration-fast)] group-hover:translate-x-0.5 group-hover:text-ink-950">
                      <ArrowIcon />
                    </span>
                  </span>
                  <span className="mt-1 line-clamp-2 px-1 pb-1 text-caption text-ink-500">
                    {widget.bestFor}
                  </span>
                </SiteLink>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-12">
          <div className={cx(CARD, 'lg:col-span-7')}>
            {/*
              The pairings with a page of their own come first and name both
              halves — somebody who arrived searching "google reviews for
              webflow" should see that exact phrase rather than a bare platform
              name. Platforms without one fall back to their hub.
            */}
            <p className="text-caption font-semibold text-ink-950">Put it on your website</p>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {combinations.map((entry) => (
                <li key={entry.platform}>
                  <SiteLink to={combinationPath(entry)} className={ROW}>
                    <span>
                      {integration.name} for{' '}
                      <span className="text-ink-950">
                        {PLATFORM_CONTENT[entry.platform].name}
                      </span>
                    </span>
                    <ArrowIcon />
                  </SiteLink>
                </li>
              ))}
              {SEO_PLATFORMS.filter(
                (platform) => !combinations.some((entry) => entry.platform === platform)
              ).map((platform) => (
                <li key={platform}>
                  <SiteLink to={`/for/${PLATFORM_CONTENT[platform].slug}`} className={ROW}>
                    <span className="text-ink-950">{PLATFORM_CONTENT[platform].name}</span>
                    <ArrowIcon />
                  </SiteLink>
                </li>
              ))}
            </ul>
          </div>

          <div className={cx(CARD, 'lg:col-span-5')}>
            <p className="text-caption font-semibold text-ink-950">
              {isDirect ? 'Other direct sources' : 'Other extension sources'}
            </p>
            <ul className="mt-4 grid grid-cols-2 gap-2">
              {siblings.map((entry) => (
                <li key={entry.slug}>
                  <SiteLink to={integrationPath(entry)} className={ROW}>
                    <span className="flex items-center gap-3">
                      <span className="grid size-8 place-items-center rounded-lg bg-white shadow-xs">
                        <SourceGlyph source={entry.id} className="size-4" />
                      </span>
                      <span className="text-ink-950">{entry.name}</span>
                    </span>
                  </SiteLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* The free builders, as one invitation rather than two bare links. */}
        <div className="relative isolate mt-4 overflow-hidden rounded-2xl bg-[linear-gradient(110deg,#eef3ff_0%,#f3efff_60%,#f7f7fa_100%)] px-7 py-7 sm:flex sm:items-center sm:justify-between sm:gap-8 sm:px-9">
          <div>
            <p className="text-title text-ink-950">
              Design the widget before you sign up.
            </p>
            <p className="mt-1.5 text-body-sm text-ink-600">
              The free builder takes your colours and layout into your account
              with you.
            </p>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-4 sm:mt-0 sm:shrink-0">
            <ButtonLink to={WIDGET_BUILDER_PATH} size="lg">
              Open the widget builder
            </ButtonLink>
            <SiteLink
              to="/tools"
              className="text-body-sm font-medium text-ink-700 underline-offset-[3px] hover:text-ink-950 hover:underline"
            >
              All free tools
            </SiteLink>
          </div>
        </div>
      </Section>

      {/* ------------------------------------------------------------ CTA */}
      {/*
        The close, on the homepage showcase's night stage: the one dark band on
        the page, so the last thing it asks for is also the thing that stands
        out. The right half draws the product in one line — this source's mark,
        reviews travelling along a wire, and a website that shows them.
      */}
      <Section size="tight" width="wide" aria-labelledby="close-heading">
        <div className="grain relative isolate overflow-hidden rounded-[2.5rem] bg-night px-7 py-12 sm:px-14 sm:py-16">
          <div
            aria-hidden
            className="grid-fade pointer-events-none absolute inset-0 -z-10 [--grid-line:rgba(255,255,255,0.05)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -left-24 -z-10 size-96 rounded-full bg-brand/40 blur-[110px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -right-20 -bottom-24 -z-10 size-96 rounded-full bg-grape/40 blur-[110px]"
          />

          <div className="relative grid items-center gap-12 lg:grid-cols-12">
            <div className="min-w-0 lg:col-span-7">
              <p className="text-caption font-semibold tracking-[0.08em] text-grape-soft uppercase">
                Free plan · no card
              </p>
              <h2
                id="close-heading"
                className="mt-4 text-[2.5rem] leading-[0.98] font-extrabold tracking-[-0.045em] text-balance text-white sm:text-6xl"
              >
                {isDirect ? (
                  <>
                    Connect your{' '}
                    <span className="bg-[linear-gradient(90deg,#8fb0ff,#b79cff)] bg-clip-text text-transparent">
                      {integration.name}
                    </span>{' '}
                    listing.
                  </>
                ) : (
                  <>
                    Bring your{' '}
                    <span className="bg-[linear-gradient(90deg,#8fb0ff,#b79cff)] bg-clip-text text-transparent">
                      {integration.name}
                    </span>{' '}
                    reviews across.
                  </>
                )}
              </h2>
              <p className="mt-5 max-w-xl text-body-lg text-white/70">
                {isDirect
                  ? 'Paste your link on the first setup screen and the reviews come in.'
                  : 'Install the free extension and run it on your own reviews page.'}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <ButtonAnchor
                  href={startUrl(`integration-${integration.slug}`)}
                  variant="inverse"
                  size="lg"
                  onClick={() => startClicked('')}
                >
                  Start free
                  <ArrowIcon />
                </ButtonAnchor>
                {isDirect ? (
                  <ButtonLink to="/pricing" variant="inverse-ghost" size="lg">
                    See pricing
                  </ButtonLink>
                ) : (
                  <ButtonAnchor
                    href={CHROME_EXTENSION_URL}
                    target="_blank"
                    variant="inverse-ghost"
                    size="lg"
                  >
                    Get the Chrome extension
                  </ButtonAnchor>
                )}
              </div>

              <ol className="mt-10 grid gap-3 sm:grid-cols-3">
                {(isDirect
                  ? ['Paste your link', 'Pick the reviews', 'Add one snippet']
                  : ['Install the extension', 'Open your reviews page', 'Pick and publish']
                ).map((step, index) => (
                  <li
                    key={step}
                    className="flex items-center gap-3 rounded-2xl bg-white/[0.06] px-4 py-3 text-body-sm font-medium text-white/85"
                  >
                    <span className="nums grid size-7 shrink-0 place-items-center rounded-full bg-white text-caption font-bold text-ink-950">
                      {index + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>

            <div className="min-w-0 lg:col-span-5">
              <CloseVisual integration={integration} />
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}

/**
 * The closing picture: this source's mark, reviews travelling along a wire,
 * and a website showing them. The wire reuses the homepage showcase's
 * `rj-flow-dash`, which settles to a still dash under reduced motion.
 */
function CloseVisual({ integration }: { integration: IntegrationContent }) {
  const reviews = sampleSetForSource(integration.id).reviews;
  const shown = [...reviews].sort((a, b) => a.content.length - b.content.length).slice(0, 2);
  const average =
    reviews.reduce((sum, review) => sum + review.rating, 0) / Math.max(1, reviews.length);

  return (
    <div aria-hidden className="relative mx-auto h-72 max-w-md sm:h-80">
      {/* The source. */}
      <span className="rj-float absolute top-6 left-0 z-10 grid size-20 -rotate-6 place-items-center rounded-[1.5rem] bg-white shadow-[0_24px_48px_-16px_rgba(0,0,0,0.6)] sm:size-24">
        <SourceGlyph source={integration.id} className="size-10 sm:size-11" />
      </span>

      {/* The wire. */}
      <svg
        viewBox="0 0 300 200"
        fill="none"
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="none"
      >
        <path
          d="M60 70 C 140 70, 120 150, 205 150"
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="1.5"
        />
        <path
          d="M60 70 C 140 70, 120 150, 205 150"
          stroke="url(#close-wire)"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="rj-flow-dash"
        />
        <defs>
          <linearGradient id="close-wire" x1="60" y1="70" x2="205" y2="150" gradientUnits="userSpaceOnUse">
            <stop stopColor="#8fb0ff" />
            <stop offset="1" stopColor="#b79cff" />
          </linearGradient>
        </defs>
      </svg>

      {/* The website. */}
      <div className="absolute right-0 bottom-0 w-[68%] overflow-hidden rounded-2xl bg-white shadow-[0_40px_80px_-24px_rgba(0,0,0,0.7)]">
        <div className="flex items-center gap-1.5 bg-ink-50 px-3 py-2">
          <span className="size-2 rounded-full bg-[#ff5f57]" />
          <span className="size-2 rounded-full bg-[#febc2e]" />
          <span className="size-2 rounded-full bg-[#28c840]" />
        </div>
        <div className="space-y-2 p-3">
          <div className="flex items-center gap-2">
            <span className="nums text-[1.35rem] leading-none font-extrabold tracking-tight text-ink-950">
              {average.toFixed(1)}
            </span>
            <ReviewStars
              rating={Math.round(average)}
              color={DEFAULT_WIDGET_SETTINGS.starColor}
              size={11}
            />
          </div>
          {shown.map((review) => (
            <div key={review.id} className="rounded-lg bg-[#f4f5f8] p-2.5">
              <p className="line-clamp-2 text-[11.5px] leading-[1.4] text-ink-800">
                {review.content}
              </p>
              <div className="mt-2 flex items-center gap-1.5">
                <ReviewerAvatar name={review.authorName} src={review.authorAvatar} size={18} />
                <span className="truncate text-[10.5px] font-semibold text-ink-950">
                  {review.authorName}
                </span>
                <SourceGlyph source={review.source} className="ml-auto size-3" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* What it becomes. */}
      <span
        className="rj-float absolute top-0 right-2 flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-2 text-caption font-medium text-white ring-0 backdrop-blur"
        style={{ animationDelay: '-3s' }}
      >
        <span className="size-1.5 rounded-full bg-positive-500" />
        Live on your site
      </span>
    </div>
  );
}

/**
 * This source's sample reviews, drawn as they would sit on somebody's website:
 * a browser window with a rating summary and four cards, and a badge saying
 * where they came from. Decoration, and marked so — the same reviews are in
 * the flow below, where they can actually be read.
 */
function HeroReviews({
  integration,
  isDirect,
}: {
  integration: IntegrationContent;
  isDirect: boolean;
}) {
  const reviews = sampleSetForSource(integration.id).reviews;
  const shown = [...reviews]
    .sort((a, b) => a.content.length - b.content.length)
    .slice(0, 4);
  const average =
    reviews.reduce((sum, review) => sum + review.rating, 0) / Math.max(1, reviews.length);

  return (
    <div aria-hidden className="relative mx-auto max-w-xl pt-8 pb-10 lg:max-w-none lg:pt-0">
      <div className="absolute inset-4 -z-10 rounded-[3rem] bg-[radial-gradient(closest-side,rgba(31,91,240,0.14),transparent)] blur-2xl" />

      {/* The window. */}
      <BrowserWindow className="lg:rotate-[1.5deg]">
        <div className="px-5 pt-5 pb-3 sm:px-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-[10px] font-semibold tracking-[0.12em] text-ink-400 uppercase">
                What our customers say
              </p>
              <p className="mt-1.5 flex items-baseline gap-2">
                <span className="nums text-[2rem] leading-none font-extrabold tracking-tight text-ink-950">
                  {average.toFixed(1)}
                </span>
                <ReviewStars
                  rating={Math.round(average)}
                  color={DEFAULT_WIDGET_SETTINGS.starColor}
                  size={15}
                />
              </p>
            </div>
            <span className="flex items-center gap-1.5 text-caption text-ink-500">
              <SourceGlyph source={integration.id} className="size-3.5" />
              {reviews.length} reviews
            </span>
          </div>

          <div className="mt-5 columns-2 gap-3">
            {shown.map((review) => (
              <div key={review.id} className="mb-3 break-inside-avoid rounded-xl bg-[#f4f5f8] p-3.5">
                <ReviewStars
                  rating={review.rating}
                  color={DEFAULT_WIDGET_SETTINGS.starColor}
                  size={11}
                />
                <p className="mt-2 line-clamp-4 text-[12.5px] leading-[1.45] text-ink-800">
                  {review.content}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <ReviewerAvatar
                    name={review.authorName}
                    src={review.authorAvatar}
                    size={22}
                  />
                  <span className="truncate text-[11.5px] font-semibold text-ink-950">
                    {review.authorName}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </BrowserWindow>

      {/* Where they came from. */}
      <div className="rj-float absolute -top-6 left-3 flex items-center gap-2.5 lg:top-[42%] rounded-2xl bg-white py-2.5 pr-4 pl-2.5 shadow-[0_1px_2px_rgba(10,10,11,0.05),0_24px_48px_-20px_rgba(20,40,120,0.45)] lg:-left-10">
        <span className="grid size-9 place-items-center rounded-xl bg-ink-25">
          <SourceGlyph source={integration.id} className="size-5" />
        </span>
        <span>
          <span className="block text-caption font-semibold text-ink-950">
            {isDirect ? `Synced from ${integration.name}` : `From ${integration.name}`}
          </span>
          <span className="flex items-center gap-1.5 text-micro text-ink-500">
            <span className="size-1.5 rounded-full bg-positive-600" />
            {isDirect ? 'Checks for new reviews' : 'Collected with the extension'}
          </span>
        </span>
      </div>

      {/* What they choose. */}
      <div
        className="rj-float absolute right-2 bottom-0 flex items-center gap-2 rounded-full bg-ink-950 py-2 pr-4 pl-2 text-caption font-medium text-white shadow-[0_24px_48px_-20px_rgba(10,10,11,0.6)] sm:-right-4"
        style={{ animationDelay: '-3s' }}
      >
        <span className="grid size-6 place-items-center rounded-full bg-white/15">
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-3"
          >
            <path d="m3.5 8.5 3 3 6-7" />
          </svg>
        </span>
        You pick what shows
      </div>
    </div>
  );
}

/** One stroke icon per use case, in the order they are written. */
function UseCaseIcon({ index }: { index: number }) {
  const paths = [
    // A storefront.
    'M4 9.5V19h16V9.5M3 5h18l-1.5 4.5a2.5 2.5 0 0 1-4.75 0 2.5 2.5 0 0 1-4.75 0 2.5 2.5 0 0 1-4.75 0L3 5Zm7 14v-5h4v5',
    // A page with a highlighted block.
    'M5 4h14v16H5zM8 8h8M8 12h8M8 16h5',
    // Stacked layers.
    'm12 4 8 4-8 4-8-4 8-4Zm-8 8 8 4 8-4M4 16l8 4 8-4',
  ];
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-5 text-brand"
      aria-hidden
    >
      <path d={paths[index % paths.length]} />
    </svg>
  );
}

const CARD =
  'rounded-2xl bg-white p-6 shadow-[0_1px_2px_rgba(10,10,11,0.04),0_12px_32px_-22px_rgba(20,40,120,0.25)]';

const ROW =
  'flex items-center justify-between gap-3 rounded-xl bg-ink-25 px-4 py-3 text-body-sm font-medium text-ink-700 transition-colors duration-[var(--duration-fast)] hover:bg-ink-50 hover:text-ink-950';
